import type { Tool, ToolHandle } from '@toolproof-core/genesis/_contract/_index';

import type { MutableToolStep } from '@toolproof-core/lib/_contract/workflow-overlay';

import { deriveCameraGraphNavigationPhase } from '@toolproof-core/visualization/_pure/camera-graph';
import type { CameraView } from '@toolproof-core/visualization/_contract/camera';
import type {
    SpaceDerivationFrame,
    TransitionPhase,
} from '@toolproof-core/visualization/derivation/_contract/derivation';

import { PRIMITIVE_CAMERA_DEFAULTS } from '@/adapters/primitive/_data/config/fragments/camera';
import { buildBooleanRodState } from '@/adapters/primitive/_pure/derivation/boolean/traceProjection';
import { deriveNaturalCameraChoreography, withNaturalCameraChoreography } from '@/adapters/primitive/_pure/derivation/natural/cameraChoreography';
import { toPrimitiveCameraViewId, withPrimitiveCameraDestination } from '@/adapters/primitive/_pure/derivation/cameraGraph';
import { buildPrimitiveRodFrame, toPrimitiveRodPlacementBasis } from '@/adapters/primitive/_pure/derivation/primitiveRod';
import { derivePrimitiveStepTransitionPhases, reversePrimitiveStepStates, toPrimitiveGeometryFramePatch } from '@/adapters/primitive/_pure/derivation/primitiveStepTransitions';
import { toPrimitiveStepWheels } from '@/adapters/primitive/_pure/derivation/primitiveSteps';
import { derivePrimitiveCameraGraph } from '@/adapters/primitive/primitiveCameraPlan';
import { CAMERA_DEFAULTS } from '@/workflow-runner/_data/config/fragments/camera';
import { WORKFLOW_ROD_DIRECTION_CATALOG } from '@/workflow-runner/_pure/workflowDirectionRules';
import { selectActiveToolStepFromTraceSnapshot } from '@/workflow-runner/_pure/model/traceSnapshotSelectors';
import {
    buildBooleanGraphFrame,
    deriveBooleanGraphPlacement,
    deriveBooleanGraphTransitionPhases,
    replaceBooleanAlignment,
    restoreBooleanAlignment,
} from '@/workflow-runner/_pure/booleanOperationGraph';
import {
    buildDescriptorRoleWheels,
    buildStandoffOverride,
    deriveNaturalLessThanTransportPhases,
    derivePlacement,
    hideWorkflowStateEntryProxies,
    mergeRodFrame,
    removeScopedRod,
    replaceScopedRod,
    resolvePlacedBooleanGraphConfig,
    resolvePrimitiveOperationRod,
    resolvePrimitiveRodConfig,
    resolveReadyPrimitiveOperationReplay,
    resolveRodDescriptor,
} from '@/workflow-runner/_pure/primitiveOperationRod';
import { applyWorkflowStateEntryConnectorVisibility } from '@/workflow-runner/_pure/workflowStateEntryConnectorVisibility';
import type { PrimitiveWheelState } from '@/adapters/primitive/_contract/config';
import type { PrimitiveStepState } from '@/adapters/primitive/_contract/steps';
import type { Config } from '@/workflow-runner/_contract/config';
import type { PrimitiveCameraPlanCoreDeriver, PrimitiveCameraPlanInput } from '@/workflow-runner/_contract/primitiveCameraPlan';
import type { PrimitiveOperationPendingReplayMove } from '@/workflow-runner/_contract/runtime';
import type { TraceSnapshot } from '@/workflow-runner/_contract/traceSnapshot';

export function derivePrimitiveCameraPlan(args: PrimitiveCameraPlanInput & {
    stepIndex: number;
    /** The step at `stepIndex` is still playing. */
    pending?: boolean;
    deriveCore?: PrimitiveCameraPlanCoreDeriver;
}) {
    const core = (args.deriveCore ?? derivePrimitiveCameraPlanCore)(args);
    if (!core) return undefined;
    const { steps, choreography, overviewView, descriptorKind, transportView, ...plan } = core;
    const stepIndex = Math.min(args.stepIndex, steps.length - 1);
    const transport = descriptorKind === 'natural-less-than' && transportView !== undefined
        && args.stepIndex === steps.length - 1 && steps.length > 1;
    // While a choreographed step plays, its phases own the camera; settled steps hold where the choreography ended.
    const activeView = transport ? transportView : !choreography ? plan.viewByStepIndex[stepIndex]
        : args.pending ? undefined
            : stepIndex === 0 ? plan.viewByStepIndex[0]
                : stepIndex === steps.length - 1 ? overviewView
                    : choreography.endViews[stepIndex] ?? overviewView;
    return {
        ...plan,
        ...(choreography ? { choreography: { actions: choreography.actions, overviewView } } : {}),
        steps,
        activeView,
        transport,
    };
}

export function derivePrimitiveCameraPlanCore(args: PrimitiveCameraPlanInput) {
    const descriptor = resolveRodDescriptor(args.toolMap.get(args.step.toolHandle));
    if (!descriptor) return undefined;
    let config = resolvePrimitiveRodConfig(args.config);
    const workflowPlacement = derivePlacement({ frame: args.frame, stepKey: args.step.stepKey, descriptor, primitiveConfig: config });
    if (!workflowPlacement) throw new Error(`Primitive camera has no placement for ${args.step.stepKey}.`);
    if (descriptor.descriptorKind === 'boolean-algebra') {
        config = resolvePlacedBooleanGraphConfig({
            frame: args.frame, stepKey: args.step.stepKey, descriptor, primitiveConfig: config,
        });
    }
    const resolution = resolvePrimitiveOperationRod({
        toolStep: args.step, preWorkflowState: args.workflowState,
        ...(args.postWorkflowState ? { postWorkflowState: args.postWorkflowState } : {}),
        primitiveConfig: config, toolMap: args.toolMap,
    });
    const wheels = buildDescriptorRoleWheels({
        descriptor, workflowState: args.workflowState, stepKey: args.step.stepKey, primitiveConfig: config,
    });
    const steps = resolution.status === 'ready' ? resolution.steps : [{
        stepKey: 'unresolved', label: 'Unresolved', description: 'Operation operands',
        keyframes: [{
            wheels, transition: 'instant' as const, events: [],
            ...(descriptor.descriptorKind === 'boolean-algebra' ? {
                booleanRod: buildBooleanRodState({ config, operation: descriptor.operation }),
            } : {}),
        }],
    }];
    const settings = args.config.adapterModeConfig.camera;
    const camera = {
        ...PRIMITIVE_CAMERA_DEFAULTS,
        natural: {
            ...PRIMITIVE_CAMERA_DEFAULTS.natural,
            ...(descriptor.descriptorKind === 'arithmetic'
                ? { resultSettlementOffset: (settings?.naturalResultSettlementOffsets
                    ?? CAMERA_DEFAULTS.naturalResultSettlementOffsets)[descriptor.operation] }
                : {}),
        },
        base: {
            ...PRIMITIVE_CAMERA_DEFAULTS.base,
            navigationMode: settings?.navigationMode ?? 'guided',
            padding: settings?.operationPadding ?? PRIMITIVE_CAMERA_DEFAULTS.base.padding,
            fov: settings?.fov ?? PRIMITIVE_CAMERA_DEFAULTS.base.fov,
            navigationDurationMs: settings?.navigationDurationMs ?? PRIMITIVE_CAMERA_DEFAULTS.base.navigationDurationMs,
        },
    };
    const placement = descriptor.descriptorKind === 'boolean-algebra'
        ? deriveBooleanGraphPlacement(workflowPlacement, config) : workflowPlacement;
    const postPlacementOverride = buildStandoffOverride({
        placement: workflowPlacement, config: args.config, workflowStateEntryGap: args.workflowStateEntryGap,
    });
    const plan = derivePrimitiveCameraGraph({
        spaceKey: args.frame.spaceKey, config, camera, steps, scope: args.scope, placement,
        postPlacementOverride,
        ...(descriptor.descriptorKind === 'boolean-algebra' ? {
            buildFocusFrame: (_step: typeof steps[number], focusWheels: readonly PrimitiveWheelState[],
                booleanRod: typeof steps[number]['keyframes'][number]['booleanRod']) => {
                if (!booleanRod) throw new Error('Boolean camera requires the unfolded graph.');
                return buildBooleanGraphFrame({
                    spaceKey: args.frame.spaceKey, scope: args.scope, config,
                    wheels: focusWheels, booleanRod, workflowPlacement, graphPlacement: placement,
                });
            },
        } : {}),
    });
    // Natural arithmetic inherits the primitive-space dispatch/flight/settlement choreography.
    const overviewView = toPrimitiveCameraViewId(args.scope, 'overview');
    const choreography = descriptor.descriptorKind === 'arithmetic' && resolution.status === 'ready'
        && camera.base.navigationMode === 'guided' && plan.graph.views[overviewView]
        ? deriveNaturalCameraChoreography({
            spaceKey: args.frame.spaceKey, config, scope: args.scope, steps, graph: plan.graph,
            viewByStepIndex: plan.viewByStepIndex, placing: { placement, postPlacementOverride }, overviewView,
            cameraCatalog: config.sceneDirectionCatalogs?.camera,
        })
        : undefined;
    let graph = choreography?.graph ?? plan.graph;
    let transportView: string | undefined;
    if (descriptor.descriptorKind === 'natural-less-than'
        && config.natural.display.comparison.naturalLessThanPresentationMode === 'travelling-eye') {
        const comparisonView = toPrimitiveCameraViewId(args.scope, 'comparison');
        const comparison = graph.views[comparisonView];
        const outputPlacement = derivePlacement({
            frame: args.frame, stepKey: args.step.stepKey, descriptor, decisionAtOutput: true,
        });
        const output = outputPlacement?.positionByRole['less-than-decision'];
        const target = placement.positionByRole['less-than-target'];
        if (!comparison || !target || !output) throw new Error(`LessThan camera has no comparison or output for ${args.step.stepKey}.`);
        const offset = settings?.naturalLessThanInspectionOffset ?? CAMERA_DEFAULTS.naturalLessThanInspectionOffset;
        if (offset.length !== 3 || !offset.every(Number.isFinite) || Math.hypot(...offset) === 0) {
            throw new Error('Invalid runner NaturalLessThan inspection offset.');
        }
        const { tangent, up, axis } = toPrimitiveRodPlacementBasis(placement);
        const worldOffset = tangent.clone().multiplyScalar(offset[0])
            .addScaledVector(up, offset[1]).addScaledVector(axis, offset[2]).toArray();
        const comparisonTarget: CameraView['target'] = { anchorKind: 'position', position: [...target] };
        transportView = toPrimitiveCameraViewId(args.scope, 'comparison-transport');
        graph = {
            stations: {
                [comparison.station]: { stationKind: 'anchored', anchor: comparisonTarget,
                    offset: worldOffset, fov: camera.base.fov },
            },
            views: {
                [comparisonView]: { station: comparison.station, target: comparisonTarget },
                [transportView]: { station: comparison.station, target: { anchorKind: 'position', position: [...output] } },
            },
            paths: [],
        };
    }
    return {
        ...plan,
        graph,
        choreography,
        transportView,
        overviewView,
        descriptorKind: descriptor.descriptorKind,
        camera,
        config,
        steps,
    };
}

function withWorkflowNaturalCameraChoreography(args: {
    base: SpaceDerivationFrame;
    fromSnapshot: TraceSnapshot | undefined;
    toSnapshot: TraceSnapshot | undefined;
    config: Config;
    toolMap: ReadonlyMap<ToolHandle, Tool>;
    workflowStateEntryGap: number;
    toolStep: Pick<MutableToolStep, 'toolHandle' | 'stepKey'>;
    scope: string;
    phases: TransitionPhase[];
    previousWheels: readonly PrimitiveWheelState[];
    pendingStep: PrimitiveStepState;
    pendingStepIndex: number;
    final: boolean;
    deriveCameraPlanCore?: PrimitiveCameraPlanCoreDeriver;
}): TransitionPhase[] {
    const runnerCamera = args.base.spaceRenderOptions?.camera;
    if (runnerCamera?.navigationMode !== 'guided' || !runnerCamera.graph || !args.fromSnapshot) return args.phases;
    const plan = derivePrimitiveCameraPlan({
        frame: args.base, step: args.toolStep, config: args.config, toolMap: args.toolMap,
        workflowState: args.fromSnapshot.position.workflowState,
        ...(args.toSnapshot ? { postWorkflowState: args.toSnapshot.position.workflowState } : {}),
        scope: args.scope, stepIndex: args.pendingStepIndex, pending: true,
        workflowStateEntryGap: args.workflowStateEntryGap,
        ...(args.deriveCameraPlanCore ? { deriveCore: args.deriveCameraPlanCore } : {}),
    });
    const actions = plan?.choreography?.actions[args.pendingStepIndex];
    if (!plan?.choreography || !actions) return args.phases;
    const graph = {
        ...runnerCamera.graph,
        stations: { ...runnerCamera.graph.stations, ...plan.graph.stations },
        views: { ...runnerCamera.graph.views, ...plan.graph.views },
    };
    const base = { ...args.base, spaceRenderOptions: { ...args.base.spaceRenderOptions, camera: { ...runnerCamera, graph } } };
    const phases = withNaturalCameraChoreography({
        base, step: args.pendingStep, phases: args.phases, actions, camera: plan.camera, config: plan.config,
        directionCatalog: plan.config.sceneDirectionCatalogs?.visuals ?? WORKFLOW_ROD_DIRECTION_CATALOG,
        previousWheels: args.previousWheels, scope: args.scope,
        transitionKeyPrefix: `primitive-operation:${args.toolStep.stepKey}:${args.pendingStep.stepKey}`,
    });
    if (args.final) {
        const navigation = deriveCameraGraphNavigationPhase(
            withPrimitiveCameraDestination(base, graph, plan.choreography.overviewView, plan.camera), undefined,
        );
        if (!navigation) throw new Error('Natural completion requires an operation Overview camera destination.');
        phases.push({
            patch: {},
            transitionOptions: { transitionKind: 'instant', transitionKey: `primitive-operation:${args.toolStep.stepKey}:completion-hold`,
                delay: plan.camera.natural.dispatchHoldDurationMs },
        }, navigation);
    }
    return phases;
}

export function derivePrimitiveOperationRodTransitionPhases(args: {
    base: SpaceDerivationFrame;
    target: SpaceDerivationFrame;
    fromSnapshot: TraceSnapshot | undefined;
    toSnapshot: TraceSnapshot | undefined;
    pendingReplayMove: PrimitiveOperationPendingReplayMove;
    config: Config;
    toolMap: ReadonlyMap<ToolHandle, Tool>;
    workflowStateEntryGap: number;
    deriveCameraPlanCore?: PrimitiveCameraPlanCoreDeriver;
}): TransitionPhase[] {
    let primitiveConfig = resolvePrimitiveRodConfig(args.config);
    const activeToolStep = selectActiveToolStepFromTraceSnapshot(args.toSnapshot);
    const activeDescriptor = activeToolStep === undefined ? undefined
        : resolveRodDescriptor(args.toolMap.get(activeToolStep.toolHandle));
    if (activeToolStep !== undefined && activeDescriptor?.descriptorKind === 'boolean-algebra') {
        primitiveConfig = resolvePlacedBooleanGraphConfig({
            frame: args.base, stepKey: activeToolStep.stepKey,
            descriptor: activeDescriptor, primitiveConfig,
        });
    }
    const replay = resolveReadyPrimitiveOperationReplay({
        fromSnapshot: args.fromSnapshot,
        toSnapshot: args.toSnapshot,
        primitiveConfig,
        toolMap: args.toolMap,
    });
    if (!replay || !args.toSnapshot) {
        return [{
            patch: toPrimitiveGeometryFramePatch(args.target),
            transitionOptions: {
                transitionKind: 'instant',
                transitionKey: `primitive-operation:settle:${args.pendingReplayMove.toPositionIndex}`,
            },
        }];
    }
    const { toolStep, resolution } = replay;
    const usesTravellingEye = primitiveConfig.natural.display.comparison.naturalLessThanPresentationMode === 'travelling-eye';
    const placement = derivePlacement({
        frame: args.base,
        stepKey: toolStep.stepKey,
        descriptor: resolution.descriptor,
        primitiveConfig,
    });
    if (!placement) {
        return [{
            patch: toPrimitiveGeometryFramePatch(args.target),
            transitionOptions: {
                transitionKind: 'instant',
                transitionKey: `primitive-operation:settle:${args.pendingReplayMove.toPositionIndex}`,
            },
        }];
    }
    const standoffOverride = buildStandoffOverride({ placement, config: args.config, workflowStateEntryGap: args.workflowStateEntryGap });

    const scope = `${args.toSnapshot.selectedTraceHandle}:${toolStep.stepKey}`;
    const committedStep = resolution.steps[args.pendingReplayMove.arithmeticProgress.committedStepIndex];
    const pendingStepIndex = args.pendingReplayMove.arithmeticProgress.pendingStepIndex;
    const pendingStep = pendingStepIndex === null ? undefined : resolution.steps[pendingStepIndex];
    if (!committedStep || !pendingStep || pendingStepIndex === null) return [];
    if (resolution.descriptor.descriptorKind === 'boolean-algebra') {
        const graphPlacement = deriveBooleanGraphPlacement(placement, primitiveConfig);
        const wrapFrame = (rod: SpaceDerivationFrame) => applyWorkflowStateEntryConnectorVisibility({
            frame: replaceBooleanAlignment(replaceScopedRod({ base: args.base, rod, scope }), scope),
            config: args.config, transformedBooleanStepKeys: new Set([toolStep.stepKey]),
        });
        if (pendingStep.stepKey === 'workflow-boolean-enter' || pendingStep.stepKey === 'workflow-boolean-exit') {
            const keyframe = pendingStep.keyframes.at(-1);
            if (keyframe?.booleanRod === undefined) throw new Error('Boolean graph transition requires primitive graph geometry.');
            const exit = pendingStep.stepKey === 'workflow-boolean-exit';
            const graphPhases = deriveBooleanGraphTransitionPhases({
                spaceKey: args.base.spaceKey, config: primitiveConfig, scope,
                wheels: keyframe.wheels, booleanRod: keyframe.booleanRod,
                workflowPlacement: placement, graphPlacement, exit, wrapFrame,
                timing: args.config.adapterModeConfig.playback.booleanGraphTransformation,
            });
            if (exit) {
                const settledFrame = replaceScopedRod({
                    base: restoreBooleanAlignment(args.base),
                    scope,
                    rod: buildPrimitiveRodFrame({
                        spaceKey: args.base.spaceKey, config: primitiveConfig, scope, placement,
                        wheels: buildDescriptorRoleWheels({
                            descriptor: resolution.descriptor,
                            workflowState: args.toSnapshot.position.workflowState,
                            stepKey: toolStep.stepKey, primitiveConfig,
                        }),
                    }),
                });
                graphPhases.push({
                    patch: toPrimitiveGeometryFramePatch(applyWorkflowStateEntryConnectorVisibility({
                        frame: args.pendingReplayMove.continuationEventKind === undefined ? args.target : settledFrame,
                        config: args.config, transformedBooleanStepKeys: new Set(),
                    })),
                    transitionOptions: { transitionKind: 'instant', transitionKey: `boolean-graph:${scope}:settle` },
                });
            }
            return graphPhases;
        }
        return derivePrimitiveStepTransitionPhases({
            spaceKey: args.base.spaceKey, config: primitiveConfig, scope,
            previousWheels: toPrimitiveStepWheels(committedStep),
            previousBooleanRod: committedStep.keyframes.at(-1)?.booleanRod,
            step: pendingStep, placement: graphPlacement,
            transitionKeyPrefix: `primitive-operation:${toolStep.stepKey}:${pendingStep.stepKey}`,
            buildInstantFrame: (wheels, booleanRod) => {
                if (booleanRod === undefined) throw new Error('Boolean playback is missing its graph.');
                // Keep fixed boundary anchors while primitive playback creates and parks travellers.
                return wrapFrame(buildBooleanGraphFrame({
                    spaceKey: args.base.spaceKey, config: primitiveConfig, scope,
                    wheels, booleanRod,
                    workflowPlacement: placement, graphPlacement,
                }));
            },
        });
    }
    const phases: TransitionPhase[] = [];
    if (resolution.descriptor.descriptorKind === 'natural-less-than' && usesTravellingEye
        && pendingStep.stepKey === 'eye-outbound') {
        const outboundFrame = replaceScopedRod({
            base: removeScopedRod(args.base, `${scope}:output-placeholder`),
            rod: buildPrimitiveRodFrame({
                spaceKey: args.base.spaceKey, config: primitiveConfig, wheels: toPrimitiveStepWheels(pendingStep),
                scope, placement, postPlacementOverride: standoffOverride,
            }),
            scope,
        });
        return deriveNaturalLessThanTransportPhases({
            frame: outboundFrame, settlementFrame: outboundFrame, stepKey: toolStep.stepKey,
            scope, wheels: toPrimitiveStepWheels(pendingStep), config: args.config, outbound: true,
        });
    }
    if (resolution.descriptor.descriptorKind === 'natural-less-than' && usesTravellingEye) {
        const comparisonBaseWheels = args.pendingReplayMove.arithmeticProgress.committedStepIndex === 0
            ? pendingStep.keyframes[0]?.wheels ?? toPrimitiveStepWheels(committedStep)
            : toPrimitiveStepWheels(committedStep);
        phases.push({
            patch: toPrimitiveGeometryFramePatch(replaceScopedRod({
                base: args.base,
                rod: buildPrimitiveRodFrame({
                    spaceKey: args.base.spaceKey,
                    config: primitiveConfig,
                    wheels: comparisonBaseWheels,
                    scope,
                    placement,
                    postPlacementOverride: standoffOverride,
                }),
                scope,
            })),
            transitionOptions: {
                transitionKind: 'instant',
                transitionKey: `natural-less-than:${toolStep.stepKey}:comparison-base`,
            },
        });
    }
    const reversingMultiplication = resolution.input.operation === 'multiplication'
        && pendingStepIndex < args.pendingReplayMove.arithmeticProgress.committedStepIndex;
    const stepPhases = derivePrimitiveStepTransitionPhases({
        spaceKey: args.base.spaceKey,
        config: primitiveConfig,
        previousWheels: toPrimitiveStepWheels(committedStep),
        ...(resolution.descriptor.descriptorKind === 'natural-less-than' && !usesTravellingEye
            ? { previousBooleanRod: committedStep.keyframes.at(-1)?.booleanRod }
            : {}),
        step: reversingMultiplication
            ? reversePrimitiveStepStates(resolution.steps, args.pendingReplayMove.arithmeticProgress.committedStepIndex, pendingStepIndex)
            : resolution.descriptor.descriptorKind === 'natural-less-than' && usesTravellingEye && pendingStep.stepKey === 'eye-return'
            ? { ...pendingStep, keyframes: pendingStep.keyframes.slice(0, -1) }
            : pendingStep,
        scope,
        placement,
        postPlacementOverride: standoffOverride,
        transitionKeyPrefix: `primitive-operation:${toolStep.stepKey}:${pendingStep.stepKey}`,
        buildInstantFrame: (wheels, _booleanRod, rodFrame) => replaceScopedRod({
            base: args.base,
            rod: rodFrame ?? buildPrimitiveRodFrame({
                spaceKey: args.base.spaceKey,
                config: primitiveConfig,
                wheels,
                scope,
                placement,
                postPlacementOverride: standoffOverride,
            }),
            scope,
        }),
    });
    phases.push(...(resolution.descriptor.descriptorKind === 'arithmetic' && !reversingMultiplication ? withWorkflowNaturalCameraChoreography({
        ...args, toolStep, scope, phases: stepPhases, previousWheels: toPrimitiveStepWheels(committedStep),
        pendingStep, pendingStepIndex, final: pendingStepIndex === resolution.steps.length - 1,
    }) : stepPhases));
    if (pendingStepIndex === resolution.steps.length - 1) {
        if (resolution.descriptor.descriptorKind === 'natural-less-than' && usesTravellingEye) {
            const landedKeyframe = pendingStep.keyframes.at(-2);
            const fadeKeyframe = pendingStep.keyframes.at(-1);
            if (!landedKeyframe || !fadeKeyframe) throw new Error('LessThan return requires landing and operand dimming keyframes.');
            const terminalWheels = landedKeyframe.wheels;
            const terminalFrame = replaceScopedRod({
                base: args.base,
                rod: buildPrimitiveRodFrame({
                    spaceKey: args.base.spaceKey,
                    config: primitiveConfig,
                    wheels: terminalWheels,
                    scope,
                    placement,
                    postPlacementOverride: standoffOverride,
                }),
                scope,
            });
            const outputPlacement = derivePlacement({
                frame: args.base,
                stepKey: toolStep.stepKey,
                descriptor: resolution.descriptor,
                decisionAtOutput: true,
            });
            if (outputPlacement === null) return phases;
            const outputStandoffOverride = buildStandoffOverride({ placement: outputPlacement, config: args.config, workflowStateEntryGap: args.workflowStateEntryGap });
            const settlementBase = replaceScopedRod({
                base: hideWorkflowStateEntryProxies({
                    frame: removeScopedRod(args.base, `${scope}:output-placeholder`),
                    stepKey: toolStep.stepKey,
                    descriptor: resolution.descriptor,
                }),
                rod: buildPrimitiveRodFrame({
                    spaceKey: args.base.spaceKey,
                    config: primitiveConfig,
                    wheels: terminalWheels.filter((wheel) => wheel.role !== 'less-than-decision'),
                    scope,
                    placement,
                    postPlacementOverride: standoffOverride,
                }),
                scope,
            });
            const landedDecisionRod = buildPrimitiveRodFrame({
                spaceKey: args.base.spaceKey,
                config: primitiveConfig,
                wheels: terminalWheels.filter((wheel) => wheel.role === 'less-than-decision'),
                scope,
                placement: outputPlacement,
                postPlacementOverride: outputStandoffOverride,
            });
            const settlementFrame = mergeRodFrame(settlementBase, landedDecisionRod);
            phases.push(...deriveNaturalLessThanTransportPhases({
                frame: terminalFrame,
                settlementFrame,
                stepKey: toolStep.stepKey,
                scope,
                wheels: terminalWheels,
                config: args.config,
            }));
            phases.push(...derivePrimitiveStepTransitionPhases({
                spaceKey: args.base.spaceKey,
                config: primitiveConfig,
                previousWheels: terminalWheels.filter((wheel) => wheel.role !== 'less-than-decision'),
                step: { ...pendingStep, keyframes: [{
                    ...fadeKeyframe,
                    wheels: fadeKeyframe.wheels.filter((wheel) => wheel.role !== 'less-than-decision'),
                }] },
                scope,
                placement,
                postPlacementOverride: standoffOverride,
                transitionKeyPrefix: `natural-less-than:${toolStep.stepKey}:operand-dim`,
                buildInstantFrame: (wheels, _booleanRod, rodFrame) => mergeRodFrame(
                    replaceScopedRod({
                        base: settlementFrame,
                        rod: rodFrame ?? buildPrimitiveRodFrame({
                            spaceKey: args.base.spaceKey, config: primitiveConfig, wheels, scope,
                            placement, postPlacementOverride: standoffOverride,
                        }),
                        scope,
                    }),
                    landedDecisionRod,
                ),
            }));
            return phases;
        }
        if (args.pendingReplayMove.continuationEventKind === undefined) {
            phases.push({
                patch: toPrimitiveGeometryFramePatch(args.target),
                transitionOptions: {
                    transitionKind: 'instant',
                    transitionKey: `primitive-operation:settle:${args.pendingReplayMove.toPositionIndex}`,
                },
            });
        }
    }
    return phases;
}
